

import React, { createContext, useContext, type ReactNode } from 'react';
import useWebSocket from 'react-use-websocket';
import type { activityType } from 'models/activity.model';
import config from 'config.json';


type WebsocketsContextValue = {
    runActivity: (activity: activityType, offline: boolean, url?: string) => void;
    getActivityOffline: () => void;
    deleteActivityOffline: (activity: activityType) => void;
    authenticatedUser: (status: boolean) => void;
    lastMessage: MessageEvent<any> | null;
    readyState: number;
};

type WebsocketsProviderProps = {
    children: ReactNode;
    enabled: boolean;
};

const WebsocketsContext = createContext<WebsocketsContextValue | undefined>(undefined);
const WS_URL = config.WS_URL;

export const WebsocketsProvider = ({ children, enabled }: WebsocketsProviderProps) => {

    const emptyFn = () => { };
    const defaultContext: WebsocketsContextValue = {
        runActivity: emptyFn,
        getActivityOffline: emptyFn,
        deleteActivityOffline: emptyFn,
        authenticatedUser: emptyFn,
        lastMessage: null,
        readyState: 3,
    };

    const socketUrl = enabled ? WS_URL : null;

    const { sendMessage, lastMessage, readyState } = useWebSocket(socketUrl, {
        share: true,
        shouldReconnect: () => enabled,
    });
    

    const runActivity = enabled ? (activity: activityType, offline: boolean, url?: string) => {
        sendMessage(JSON.stringify({ id: 'run', data: { activityId: activity.id, offline, url } }));
    } : emptyFn;

    const getActivityOffline = enabled ? () => {
        sendMessage(JSON.stringify({ id: 'list' }));
    } : emptyFn;

    const deleteActivityOffline = enabled ? (activity: activityType) => {
        sendMessage(JSON.stringify({ id: 'delete', data: activity.id }));
    } : emptyFn;

    const authenticatedUser = enabled ? (status: boolean) => {
        sendMessage(JSON.stringify({ id: 'authenticated', data: status }));
    } : emptyFn;

    return (
        <WebsocketsContext.Provider
            value={enabled ? { runActivity, getActivityOffline, deleteActivityOffline, authenticatedUser, lastMessage, readyState } : defaultContext}
        >
            {children}
        </WebsocketsContext.Provider>
    );
};

export const useWebsocketsService = (): WebsocketsContextValue => {
    const context = useContext(WebsocketsContext);
    if (!context) {
        throw new Error('useWebsocketsService must be used within a WebsocketsProvider');
    }
    return context;
};